/**
 * VÉRAULT Member Suite — StaysShelf (Wave 27.3 · Phase 4c).
 * ────────────────────────────────────────────────────────────────────
 * Editorial hotels · villas · resorts · ryokans · cruises.
 *
 *   Hero imagery · address · check-in/out · perks · room category ·
 *   contact info · mini-map preview · check-in instructions.
 *
 * Phase 4c polish:
 *   ▸ StayMiniMap — quiet 21:8 SVG showing the property location
 *     as a pin on a paper-tint background. Same palette as RouteMap.
 *     Veil-safe (suppressed if lat/lng missing).
 *   ▸ PerksChips — quiet inline pill row.
 *   ▸ CheckInInstructions — collapsible italic prose block, opens
 *     with a gold ▸ Read check-in details affordance.
 */
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Copy, ChevronDown, Phone, Globe, Sparkles, KeyRound, Maximize2, X } from "lucide-react";
import { withEventStates, stateLabel } from "../../../../utils/eventState";

function StayStateChip({ b }) {
  if (!b || !b._state) return null;
  const m = stateLabel(b);
  return (
    <span className={`font-mono text-[8.5px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full ${m.cls}`} data-testid={`stay-state-${b.id}`}>
      {m.label}
    </span>
  );
}

// Parse date-only strings (YYYY-MM-DD) at local noon so browser timezone
// never shifts the calendar day (the classic off-by-one bug).
function parseLocal(iso) {
  if (!iso) return null;
  const s = String(iso);
  return new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T12:00:00` : s);
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return parseLocal(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  } catch { return iso; }
}

function nights(checkIn, checkOut) {
  try {
    const a = parseLocal(checkIn).getTime();
    const b = parseLocal(checkOut).getTime();
    if (!a || !b) return null;
    const n = Math.round((b - a) / (24 * 3600 * 1000));
    return n > 0 ? n : null;
  } catch { return null; }
}

function copyText(text) {
  if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
}

// ─── StayMiniMap ─────────────────────────────────────────────────────
// Tiny SVG canvas (same palette as RouteMap) showing the property as a
// numbered "01" pin centered on a paper background. Tappable to open
// a calm full-screen overlay with the same map at larger scale.
function StayMiniMap({ stay, testid, onExpand }) {
  const lat = stay?.latitude;
  const lng = stay?.longitude;
  // Suppress if no coords (veil-safe — coords might be hidden).
  if (lat == null || lng == null) return null;

  const cx = 50;

  return (
    <button
      type="button"
      onClick={onExpand}
      className="block w-full rounded-md overflow-hidden border border-[rgba(0,0,0,0.06)] bg-[hsl(var(--background-paper))] group relative hover:border-[hsl(var(--accent-gold)/0.4)] transition-colors"
      data-testid={testid}
      data-stay-id={stay?.id}
      aria-label="Expand map"
    >
      <svg
        viewBox="0 0 100 38"
        preserveAspectRatio="xMidYMid slice"
        className="block w-full aspect-[21/8]"
        aria-hidden="true"
      >
        <defs>
          <pattern id={`dots-${stay?.id}`} x="0" y="0" width="3" height="3" patternUnits="userSpaceOnUse">
            <circle cx="0.5" cy="0.5" r="0.18" fill="hsl(var(--foreground-muted))" opacity="0.18" />
          </pattern>
        </defs>
        <rect x="0" y="0" width="100" height="38" fill="hsl(var(--background-paper))" />
        <rect x="0" y="0" width="100" height="38" fill={`url(#dots-${stay?.id})`} />

        <text x="2" y="5" fontSize="2" fill="hsl(var(--foreground-muted))" opacity="0.4" fontFamily="ui-monospace, monospace">
          N {Number(lat).toFixed(3)}°
        </text>
        <text x="100" y="35" textAnchor="end" fontSize="2" fill="hsl(var(--foreground-muted))" opacity="0.4" fontFamily="ui-monospace, monospace">
          W {Math.abs(Number(lng)).toFixed(3)}°
        </text>

        <circle cx={cx} cy="19" r="8" fill="hsl(var(--accent-gold))" opacity="0.12" />
        <circle cx={cx} cy="19" r="4" fill="hsl(var(--accent-gold))" opacity="0.28" />
        <circle cx={cx} cy="19" r="2.2" fill="hsl(var(--accent-gold))" />
        <text x={cx} y="19.8" textAnchor="middle" fontSize="2.2" fill="white" fontFamily="ui-monospace, monospace" fontWeight="600">
          01
        </text>

        <text x={cx} y="32" textAnchor="middle" fontSize="2.8" fill="hsl(var(--foreground))" fontFamily="serif">
          {(stay?.vendor || "").substring(0, 40)}
        </text>
      </svg>
      {/* Quiet expand affordance — only visible on hover */}
      <span className="absolute top-2 right-2 inline-flex items-center justify-center size-7 rounded-full bg-white/80 backdrop-blur-sm border border-[rgba(0,0,0,0.06)] opacity-0 group-hover:opacity-100 transition-opacity">
        <Maximize2 className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
      </span>
    </button>
  );
}

// ─── StayMapOverlay ──────────────────────────────────────────────────
// Full-screen calm overlay surfaced when the mini-map is tapped.
// Shows the same gold pin at larger scale + a quiet dashed line back
// to a `previousStay` (chains visually with the prior stop in the
// trip), or just the property in isolation when first stop.
function StayMapOverlay({ stay, previousStay, onClose }) {
  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!stay) return null;
  const hasChain = previousStay
    && typeof previousStay.latitude === "number"
    && typeof previousStay.longitude === "number";

  // Project two points onto the canvas with auto-fit padding.
  // For single point: dead center. For chain: place previous left, current right.
  const W = 100; const H = 56;
  const curX = hasChain ? 72 : 50;
  const prevX = 28;
  const Y = 28;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-5 sm:p-10"
        onClick={onClose}
        data-testid="stay-map-overlay"
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-4xl rounded-lg overflow-hidden bg-[hsl(var(--background))] shadow-2xl"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close map"
            className="absolute top-3 right-3 z-10 inline-flex items-center justify-center size-9 rounded-full bg-white/85 backdrop-blur border border-[rgba(0,0,0,0.06)] hover:bg-white"
            data-testid="stay-map-overlay-close"
          >
            <X className="size-4" strokeWidth={1.5} />
          </button>
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" className="block w-full aspect-[16/9]">
            <defs>
              <pattern id={`overlay-dots-${stay.id}`} x="0" y="0" width="2" height="2" patternUnits="userSpaceOnUse">
                <circle cx="0.4" cy="0.4" r="0.14" fill="hsl(var(--foreground-muted))" opacity="0.16" />
              </pattern>
            </defs>
            <rect x="0" y="0" width={W} height={H} fill="hsl(var(--background-paper))" />
            <rect x="0" y="0" width={W} height={H} fill={`url(#overlay-dots-${stay.id})`} />

            {/* Compass marks (overlay scale) */}
            <text x="3" y="5" fontSize="2" fill="hsl(var(--foreground-muted))" opacity="0.45" fontFamily="ui-monospace, monospace">
              N {Number(stay.latitude).toFixed(4)}°
            </text>
            <text x="97" y="54" textAnchor="end" fontSize="2" fill="hsl(var(--foreground-muted))" opacity="0.45" fontFamily="ui-monospace, monospace">
              W {Math.abs(Number(stay.longitude)).toFixed(4)}°
            </text>

            {/* Optional chain line from previous stay */}
            {hasChain && (
              <>
                <line
                  x1={prevX} y1={Y} x2={curX} y2={Y}
                  stroke="hsl(var(--accent-gold))" strokeWidth="0.5"
                  strokeDasharray="1 1" opacity="0.55"
                />
                <circle cx={prevX} cy={Y} r="5" fill="hsl(var(--foreground-muted))" opacity="0.1" />
                <circle cx={prevX} cy={Y} r="2.5" fill="hsl(var(--foreground-muted))" opacity="0.35" />
                <text x={prevX} y={Y + 5.5} textAnchor="middle" fontSize="1.8" fill="hsl(var(--foreground-muted))" fontFamily="ui-monospace, monospace">
                  PREV
                </text>
                <text x={prevX} y={Y + 8.5} textAnchor="middle" fontSize="2" fill="hsl(var(--foreground-secondary))" fontFamily="serif">
                  {(previousStay.vendor || "").substring(0, 24)}
                </text>
              </>
            )}

            {/* Current stay — main pin */}
            <circle cx={curX} cy={Y} r="12" fill="hsl(var(--accent-gold))" opacity="0.12" />
            <circle cx={curX} cy={Y} r="6" fill="hsl(var(--accent-gold))" opacity="0.3" />
            <circle cx={curX} cy={Y} r="3" fill="hsl(var(--accent-gold))" />
            <text x={curX} y={Y + 1} textAnchor="middle" fontSize="2.6" fill="white" fontFamily="ui-monospace, monospace" fontWeight="600">
              {hasChain ? "02" : "01"}
            </text>
            <text x={curX} y={Y + 9} textAnchor="middle" fontSize="3" fill="hsl(var(--foreground))" fontFamily="serif">
              {(stay.vendor || "").substring(0, 36)}
            </text>
            {stay.address && (
              <text x={curX} y={Y + 13.5} textAnchor="middle" fontSize="2" fill="hsl(var(--foreground-muted))" fontStyle="italic" fontFamily="serif">
                {stay.address.substring(0, 50)}
              </text>
            )}
          </svg>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── PerksChips ──────────────────────────────────────────────────────
function PerksChips({ perks, testid }) {
  if (!perks?.length) return null;
  return (
    <div className="mt-4" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-2 flex items-center gap-1.5">
        <Sparkles className="size-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
        Included perks
      </p>
      <div className="flex flex-wrap gap-1.5">
        {perks.map((p, i) => (
          <span
            key={`${p}-${i}`}
            className="inline-block px-2.5 py-1 rounded-full bg-[hsl(var(--accent-gold)/0.08)] border border-[hsl(var(--accent-gold)/0.22)] font-mono text-[11px] tracking-[0.02em] text-[hsl(var(--foreground-secondary))]"
            data-testid={`perk-${i}`}
          >
            {p}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── CheckInInstructions ─────────────────────────────────────────────
function CheckInInstructions({ text, testid }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]"
        data-testid={testid}
      >
        <KeyRound className="size-3" strokeWidth={1.5} />
        {open ? "Hide check-in details" : "Read check-in details"}
        <ChevronDown className={`size-3 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
          className="mt-3 rounded-md bg-[hsl(var(--background-paper))] border-l-2 border-[hsl(var(--accent-gold)/0.6)] px-4 py-3"
          data-testid={`${testid}-content`}
        >
          <p className="font-accent italic text-[13.5px] leading-relaxed text-[hsl(var(--foreground-secondary))]">
            {text}
          </p>
        </motion.div>
      )}
    </div>
  );
}

function StayCard({ b, previousStay }) {
  const [open, setOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const n = nights(b.start_date, b.end_date);
  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-md overflow-hidden bg-white/65 backdrop-blur-[2px] border transition-opacity ${b._next ? "border-[hsl(var(--accent-gold))]/50 ring-1 ring-[hsl(var(--accent-gold))]/25" : "border-[rgba(0,0,0,0.06)]"} ${b._state === "completed" ? "opacity-60" : ""}`}
      data-testid={`stay-card-${b.id}`}
    >
      {b.image_url ? (
        <div className="relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
          <img
            src={b.image_url}
            alt={b.vendor || "stay"}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 text-white">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] opacity-85 flex items-center gap-2">
              {b.booking_type === "villa" ? "Villa" : b.booking_type === "ryokan" ? "Ryokan" : b.booking_type === "cruise" ? "Cruise" : b.booking_type === "resort" ? "Resort" : "Hotel"}
              {n ? ` · ${n} ${n === 1 ? "night" : "nights"}` : ""}
              <StayStateChip b={b} />
            </p>
            <h3 className="mt-1.5 font-serif text-[22px] sm:text-[28px] leading-tight tracking-tight">
              {b.vendor || b.description}
            </h3>
            {b.room_category && (
              <p className="mt-1 font-accent italic text-[14px] opacity-90">
                {b.room_category}
              </p>
            )}
          </div>
        </div>
      ) : (
        /* No hero image — render the property name as a quiet header. */
        <div className="px-5 sm:px-6 pt-5 sm:pt-6" data-testid={`stay-card-${b.id}-header`}>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))] flex items-center gap-2">
            {b.booking_type === "villa" ? "Villa" : b.booking_type === "ryokan" ? "Ryokan" : b.booking_type === "cruise" ? "Cruise" : b.booking_type === "resort" ? "Resort" : "Hotel"}
            {n ? ` · ${n} ${n === 1 ? "night" : "nights"}` : ""}
            <StayStateChip b={b} />
          </p>
          <h3 className="mt-1.5 font-serif text-[22px] sm:text-[26px] leading-tight tracking-tight">
            {b.vendor || b.description}
          </h3>
          {b.room_category && (
            <p className="mt-1 font-accent italic text-[14px] text-[hsl(var(--foreground-muted))]">
              {b.room_category}
            </p>
          )}
        </div>
      )}
      <div className="px-5 sm:px-6 py-4 sm:py-5">
        <div className="flex flex-wrap gap-x-8 gap-y-3 text-[13.5px]">
          {b.start_date && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Check-in</p>
              <p className="mt-0.5 font-serif text-[16px]">{fmtDate(b.start_date)}</p>
            </div>
          )}
          {b.end_date && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Check-out</p>
              <p className="mt-0.5 font-serif text-[16px]">{fmtDate(b.end_date)}</p>
            </div>
          )}
          {b.confirmation_number && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Confirmation</p>
              <p className="mt-0.5 font-mono text-[13px] tabular-nums inline-flex items-center gap-2">
                <span>{b.confirmation_number}</span>
                <button onClick={() => copyText(b.confirmation_number)} className="text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]" title="Copy" data-testid={`copy-${b.id}`}>
                  <Copy className="size-3" strokeWidth={1.5} />
                </button>
              </p>
            </div>
          )}
        </div>
        {b.address && (
          <p className="mt-4 flex items-start gap-1.5 text-[13.5px] text-[hsl(var(--foreground-secondary))]">
            <MapPin className="size-3.5 mt-0.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
            <span>{b.address}</span>
          </p>
        )}

        {/* Wave 27.3 · Phase 4c — mini-map preview (tappable to full-screen) */}
        {(b.latitude != null && b.longitude != null) && (
          <div className="mt-4">
            <StayMiniMap stay={b} testid={`stay-minimap-${b.id}`} onExpand={() => setMapOpen(true)} />
          </div>
        )}
        {mapOpen && (
          <StayMapOverlay
            stay={b}
            previousStay={previousStay}
            onClose={() => setMapOpen(false)}
          />
        )}

        <div className="mt-4 flex flex-wrap items-center gap-4 text-[12.5px] font-mono">
          {b.phone && (
            <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1.5 text-[hsl(var(--accent-gold))] hover:underline">
              <Phone className="size-3" strokeWidth={1.5} />{b.phone}
            </a>
          )}
          {b.website && (
            <a href={b.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[hsl(var(--accent-gold))] hover:underline">
              <Globe className="size-3" strokeWidth={1.5} />Property site
            </a>
          )}
        </div>

        {/* Wave 27.3 · Phase 4c — perks chips */}
        <PerksChips perks={b.perks} testid={`stay-perks-${b.id}`} />

        {/* Wave 27.3 · Phase 4c — check-in instructions */}
        <CheckInInstructions text={b.check_in_instructions} testid={`stay-checkin-${b.id}`} />

        {b.client_notes && (
          <button
            onClick={() => setOpen((v) => !v)}
            className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]"
          >
            {open ? "Hide notes" : "Read property notes"}
            <ChevronDown className={`size-3 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
          </button>
        )}
        {open && b.client_notes && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            transition={{ duration: 0.4 }}
            className="mt-3 font-accent italic text-[13.5px] text-[hsl(var(--foreground-secondary))] border-l-2 border-[hsl(var(--accent-gold)/0.4)] pl-3"
          >
            {b.client_notes}
          </motion.p>
        )}
      </div>
    </motion.article>
  );
}

export default function StaysShelf({ items = [] }) {
  const [showCompleted, setShowCompleted] = React.useState(false);
  if (!items || items.length === 0) return null;
  const annotated = withEventStates(items, (b) => b.start_date, (b) => b.end_date || b.start_date);
  const active = annotated.filter((b) => b._state !== "completed");
  const completed = annotated.filter((b) => b._state === "completed");
  const cardFor = (b, i, source) => {
    const prev = source.slice(0, i).reverse()
      .find((p) => typeof p.latitude === "number" && typeof p.longitude === "number" && p.id !== b.id);
    return <StayCard key={b.id} b={b} previousStay={prev} />;
  };
  return (
    <section id="stays" data-testid="essentials-stays" className="scroll-mt-24">
      <header className="mb-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
          Stays
        </p>
        <h2 className="mt-1.5 font-serif text-[22px] sm:text-[26px] leading-tight tracking-tight">
          Where you'll rest.
        </h2>
      </header>
      <div className="space-y-5 sm:space-y-6">
        {active.map((b, i) => cardFor(b, i, active))}
      </div>
      {completed.length > 0 && (
        <div className="mt-5">
          <button type="button" onClick={() => setShowCompleted((v) => !v)} data-testid="stays-completed-toggle"
            className="w-full flex items-center justify-between gap-2 rounded-md border border-[rgba(0,0,0,0.08)] bg-white/40 px-4 py-3 hover:bg-[hsl(var(--accent-gold))]/[0.04] transition-colors">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              Completed stays · {completed.length}
            </span>
            <ChevronDown className={`size-4 text-[hsl(var(--foreground-muted))] transition-transform ${showCompleted ? "rotate-180" : ""}`} strokeWidth={1.5} />
          </button>
          {showCompleted && (
            <div className="space-y-5 sm:space-y-6 mt-4" data-testid="stays-completed-list">
              {completed.map((b, i) => cardFor(b, i, completed))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
